import React, { useState, useCallback, useMemo } from 'react';
import { Typography } from '../../../interfaces/typography';
import { AgentType, PartyRole, Platform, PLATFORM_TO_HUMAN_READABLE } from '../../../interfaces/db';
import { PROSPECT_INFO_COLUMNS, SESSION_METRICS_COLUMNS } from 'interfaces/table';
import { X, Share2, Phone, ExternalLink, FileText, Globe, Webhook } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { SessionDetailView, viewHasMinimalHeader } from '../../History/views';
import { SessionStatus } from '../../SessionStatus/SessionStatus';
import { useUserData } from '../../../lib/redux/users/user-data-hook';
import { SessionDataResult } from 'interfaces/sessions';
import { getSessionNameFromState, getProspectNameFromSessionData, canUserEditSessionMetrics } from '../../../utils/session';
import { getServicesManager } from '../../../services';
import { useColumnOptions } from '../../../lib/redux/filters/options/options-hook';
import { ClickableMetric } from '../ClickableMetric';
import { useDispatch } from 'react-redux';
import { sessionDetailsSlice } from '../../../lib/redux/sessions/data/data-slice';
import { updateSessionMetricInList } from '../../../lib/redux/sessions/lists/list-slice';
import { WebhookPreviewModal } from '../WebhookPreviewModal';

interface TranscriptHeaderProps {
  data: SessionDataResult;
  onClose?: () => void;
  onCopyTranscript?: () => void;
  view: SessionDetailView;
}

export const TranscriptHeader: React.FC<TranscriptHeaderProps> = ({
  data,
  onClose,
  view
}) => {
  const [showCopied, setShowCopied] = useState(false);
  const [showPhoneCopied, setShowPhoneCopied] = useState(false);
  const [showTranscriptCopied, setShowTranscriptCopied] = useState(false);
  const [showPublicLinkCopied, setShowPublicLinkCopied] = useState(false);
  const [isWebhookModalOpen, setIsWebhookModalOpen] = useState(false);

  // Get the current user and visible accounts from hooks
  const { user, allVisibleUsers, isAdmin } = useUserData();

  // Get column options for dispositions, sentiments, and purposes
  const { getColumnOptions } = useColumnOptions();
  const dispatch = useDispatch();

  // Check if user can edit this session's metrics
  const canEdit = canUserEditSessionMetrics(data, user?.user_id, isAdmin);

  // Get options for each metric type
  const dispositionOptions = getColumnOptions(SESSION_METRICS_COLUMNS.USER_DISPOSITION);
  const sentimentOptions = getColumnOptions(SESSION_METRICS_COLUMNS.USER_SENTIMENT);
  const purposeOptions = getColumnOptions(SESSION_METRICS_COLUMNS.USER_PURPOSE);

  // Handler for updating session metrics
  const handleMetricUpdate = useCallback(async (
    metricType: 'disposition' | 'sentiment' | 'purpose',
    value: string | null
  ) => {
    // First update the Redux store optimistically
    const sessionId = data.session.session_id;
    const updates: any = {};
    
    switch (metricType) {
      case 'disposition':
        updates[SESSION_METRICS_COLUMNS.USER_DISPOSITION] = value;
        break;
      case 'sentiment':
        updates[SESSION_METRICS_COLUMNS.USER_SENTIMENT] = value;
        break;
      case 'purpose':
        updates[SESSION_METRICS_COLUMNS.USER_PURPOSE] = value;
        break;
    }

    dispatch(sessionDetailsSlice.actions.updateSessionMetric({
      sessionId,
      sessionMetric: updates
    }));

    // Also update the session in the lists
    dispatch(updateSessionMetricInList({
      sessionId,
      updates
    }));

    // Then call the API
    try {
      const servicesManager = getServicesManager();
      const apiUpdates: any = {};

      if (metricType === 'disposition') apiUpdates.userDisposition = value;
      if (metricType === 'sentiment') apiUpdates.userSentiment = value;
      if (metricType === 'purpose') apiUpdates.userPurpose = value;

      await servicesManager.updateSessionMetrics(sessionId, apiUpdates);
    } catch (error) {
      // TODO: Consider reverting the optimistic update or showing an error message
    }
  }, [data.session.session_id, dispatch]);

  const disposition = data.sales_metric?.user_disposition;
  const sentiment = data.sales_metric?.user_sentiment;
  const purpose = data.sales_metric?.user_purpose;

  const formatPhoneNumber = (phoneNumber: string) => {
    if (phoneNumber.charAt(0) === '1') {
      return phoneNumber.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '+$1 ($2) $3-$4');
    }
    return phoneNumber.replace(/(\d{1,4})(\d{2,4})(\d{4})/, '+$1 $2 $3');
  };

  const getTitle = () => {
    let title = data.prospect_info?.prospect_title;
    const platform = data.prospect_info?.platform;

    if (platform === Platform.SALESLOFT) {
      title = `${data.prospect_info?.prospect_seniority ?? ''} ${data.prospect_info?.prospect_title ?? ''}`.trim();
    } else if (platform === Platform.OUTREACH) {
      title = data.prospect_info?.prospect_title || data.prospect_info?.prospect_occupation;
    }

    return title?.trim() || null;
  };

  const getPlatform = () => {
    return data.prospect_info?.[PROSPECT_INFO_COLUMNS.PLATFORM]
  }

  const getCompanyName = () => {
    return data.prospect_info?.[PROSPECT_INFO_COLUMNS.COMPANY_NAME]
  }


  const getSessionUserId = () => {
    if (!data.session_agents) return null
    if (data.session_agents.creator_agent_type !== AgentType.USER) return null
    return data.session_agents.creator_agent_id
  }

  const getRepNumber = () => {
    const agentAddress = data.session.agent_address;
    return agentAddress && agentAddress !== '-' ? agentAddress : null;
  };

  const constructPlatformLink = (platform: Platform, personId: string | null): string | null => {
    if (!personId) return null;
    if (platform === Platform.SALESLOFT) {
      return `https://app.salesloft.com/app/people/${personId}`;
    } else if (platform === Platform.OUTREACH) {
      return `https://web.outreach.io/prospects/${personId}/overview`;
    } else if (platform === Platform.APOLLO) {
      return `https://app.apollo.io/#/contacts/${personId}`;
    }
    return null;
  }

  const getPlatformUrl = (): string | null => {
    if (!data.prospect_info) return null;
    
    const platform = data.prospect_info[PROSPECT_INFO_COLUMNS.PLATFORM];
    const personId = data.prospect_info[PROSPECT_INFO_COLUMNS.PLATFORM_PERSON_ID] ?? data.prospect_info[PROSPECT_INFO_COLUMNS.XREF_PERSON_ID];
    const platformLink = data.prospect_info[PROSPECT_INFO_COLUMNS.PLATFORM_PERSON_URL] ?? data.prospect_info[PROSPECT_INFO_COLUMNS.XREF_PERSON_URL];
    if (!platform || (!personId && !platformLink)) return null;
    return platformLink ?? constructPlatformLink(platform, personId);
  }

  const handleCopyLink = async () => {
    try {
      const url = `https://${window.location.hostname}/transcripts?id=${data.session.session_id}`;
      await navigator.clipboard.writeText(url);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const handleCopyPhone = async () => {
    if (!data.session.customer_address) return;
    try {
      await navigator.clipboard.writeText(data.session.customer_address);
      setShowPhoneCopied(true);
      setTimeout(() => setShowPhoneCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy phone number:', err);
    }
  };

  const handleCopyTranscript = async () => {
    try {
      // Format each turn into text
      const transcriptText = data.turns
        .filter(turn => turn.text.trim() !== '') // Skip empty turns
        .map(turn => {
          const speaker = turn.role === PartyRole.REP ? 'Rep' : 'Prospect';
          return `${speaker}: ${turn.text.trim()}`;
        })
        .join('\n');

      // Add header with call details
      const headerParts = [
        `Date: ${data.session.started_at.toLocaleString('en-US', { 
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hour12: true
        })}`,
      ];

      if (data.prospect_info?.[PROSPECT_INFO_COLUMNS.COMPANY_NAME]) {
        headerParts.push(`Company: ${data.prospect_info[PROSPECT_INFO_COLUMNS.COMPANY_NAME]}`);
      }

      const fullTranscript = `${headerParts.join('\n')}\n\n${transcriptText}`;
      
      await navigator.clipboard.writeText(fullTranscript);
      setShowTranscriptCopied(true);
      setTimeout(() => setShowTranscriptCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy transcript:', err);
    }
  };

  const handleCopyPublicLink = async () => {
    try {
      const servicesManager = getServicesManager();
      const accessToken = await servicesManager.getSessionAccessToken(data.session.session_id);

      if (accessToken?.session_access_token) {
        const url = `https://${window.location.hostname}/view?access_token=${accessToken.session_access_token}&session_id=${data.session.session_id}`;
        await navigator.clipboard.writeText(url);
        setShowPublicLinkCopied(true);
        setTimeout(() => setShowPublicLinkCopied(false), 2000);
      } else {
        console.error('Failed to generate access token');
      }
    } catch (err) {
      console.error('Failed to copy public URL:', err);
    }
  };

  const getDateFormat = (): Intl.DateTimeFormatOptions => {
    if (viewHasMinimalHeader(view)) {
      return { month: 'short', day: 'numeric' }
    }
    return { month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true }
  }

  const sessionUserId = getSessionUserId()
  const companyName = getCompanyName()
  const title = getTitle()
  const platform = getPlatform()
  const repNumber = getRepNumber()
  const prospectName = getProspectNameFromSessionData(data)

  // Check if session has webhooks by looking for webhook status fields in state
  const webhookInfos = useMemo(() => {
    // Check if this is a bot call
    const isBot = data.session_agents?.creator_agent_type === AgentType.VOICE_BOT;
    if (!isBot) return [];

    // Get session state
    const states = data.session_state?.states || {};

    // Look for webhook status fields (pattern: __webhook_{webhook_id}__)
    const webhookKeys = Object.keys(states).filter(key => key.startsWith('__webhook_') && key.endsWith('__'));

    if (webhookKeys.length === 0) return [];

    // Extract webhook info for ALL webhooks
    return webhookKeys.map(webhookKey => {
      const match = webhookKey.match(/__webhook_(.+)__/);
      const webhookId = match ? match[1] : webhookKey.replace('__webhook_', '').replace(/__$/, '');

      return {
        webhookId,
        status: states[webhookKey]
      };
    });
  }, [data]);

  return (
    <div className="w-full border-b border-gray-100">
      <div className="flex flex-col p-2">
        {/* Name, title, date, and action buttons */}
        <div className="flex justify-between items-start">
          <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 max-w-[75%]">
            {/* Prospect name - prioritize over title */}
            {prospectName && (
              <div className="flex items-center max-w-[300px] min-w-0">
                <Typography variant="h5" fontWeight="600" color="#1F2937" className="truncate w-full">
                  {prospectName}
                </Typography>
              </div>
            )}
            
            {/* Title - show if no prospect name or as secondary info */}
            {title && !prospectName && (
              <div className="flex items-center max-w-[300px] min-w-0">
                <Typography variant="mediumParagraph" color="gray" className="truncate w-full">
                  {title}
                </Typography>
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <Typography variant="mediumParagraph" color="gray">
                {data.session.started_at.toLocaleString('en-US', getDateFormat())}
              </Typography>
            </div>
          </div>
          
          {/* Action buttons */}
          <div className="flex items-center gap-2">
            {/* Session status */}
            {user && sessionUserId && (
              <div className="flex items-center">
                <SessionStatus 
                  isMainView={true} 
                  sessionId={data.session.session_id} 
                  sessionUserId={sessionUserId} 
                  type={'sessionData'} 
                  viewerUserId={user.user_id} 
                  reviewIsOpen={data.review_status?.is_open}
                  reviewOpenedByUser={data.review_status?.opening_user_id === user.user_id}
                />
              </div>
            )}

            {/* Webhook button - show if bot call with webhooks */}
            {webhookInfos.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setIsWebhookModalOpen(true)}
                  className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  aria-label="View webhook"
                >
                  <Webhook size={16} />
                  {webhookInfos.length > 1 && (
                    <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                      {webhookInfos.length}
                    </span>
                  )}
                </button>
              </div>
            )}

            {/* Copy transcript button */}
            {(
              <div className="relative">
                <button
                  onClick={handleCopyTranscript}
                  className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  aria-label="Copy transcript"
                >
                  <FileText size={16} />
                </button>
                
                {/* Copy notification */}
                {/* @ts-ignore - Ignoring the AnimatePresence type error as it's a known issue with framer-motion types */}
                <AnimatePresence>
                  {showTranscriptCopied && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="absolute top-full right-0 mt-1 px-2 py-1 
                        bg-gray-800 text-white text-xs rounded whitespace-nowrap z-50"
                    >
                      <Typography variant="smallParagraph" color="white">
                        Transcript copied!
                      </Typography>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
            
            {/* Share button */}
            <div className="relative">
              <button 
                onClick={(view === SessionDetailView.MAIN) ? handleCopyLink : () => {
                  window.open(`https://${window.location.hostname}/transcripts?id=${data.session.session_id}`, '_blank');
                }}
                className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Share transcript"
              >
                <Share2 size={16} />
              </button>
              
              {/* Copy notification */}
              {/* @ts-ignore - Ignoring the AnimatePresence type error as it's a known issue with framer-motion types */}
              <AnimatePresence>
                {showCopied && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="absolute top-full right-0 mt-1 px-2 py-1 
                      bg-gray-800 text-white text-xs rounded whitespace-nowrap z-50"
                  >
                    <Typography variant="smallParagraph" color="white">
                      URL copied!
                    </Typography>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Public Share button - Only show for admins */}
            {isAdmin && (
              <div className="relative">
                <button
                  onClick={handleCopyPublicLink}
                  className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                  aria-label="Share publicly"
                >
                  <Globe size={16} />
                </button>

                {/* Copy notification */}
                {/* @ts-ignore - Ignoring the AnimatePresence type error as it's a known issue with framer-motion types */}
                <AnimatePresence>
                  {showPublicLinkCopied && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="absolute top-full right-0 mt-1 px-2 py-1
                        bg-gray-800 text-white text-xs rounded whitespace-nowrap z-50"
                    >
                      <Typography variant="smallParagraph" color="white">
                        Public link copied!
                      </Typography>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}

            {/* Close button */}
            {onClose && (
              <button 
                onClick={onClose}
                className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Close transcript"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Group all non-name rows together */}
        <div className="flex flex-col gap-1.5">
          {/* Contact info, company, platform link */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {/* Phone number with copy functionality */}
            {data.session.customer_address && (
              <div className="relative">
                <button 
                  onClick={handleCopyPhone}
                  className="flex items-center gap-1 hover:bg-gray-50 rounded px-1 py-0.5 transition-colors group"
                >
                  <Phone size={14} className="text-gray-500" />
                  <Typography variant="mediumParagraph">
                    {formatPhoneNumber(data.session.customer_address)}
                  </Typography>
                </button>
                
                {/* Copy notification */}
                {/* @ts-ignore - Ignoring the AnimatePresence type error as it's a known issue with framer-motion types */}
                <AnimatePresence>
                  {showPhoneCopied && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="absolute top-full left-0 mt-1 px-2 py-1 
                        bg-gray-800 text-white text-xs rounded whitespace-nowrap z-50"
                    >
                      <Typography variant="smallParagraph" color="white">
                        Copied!
                      </Typography>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
            
            {/* Rep number */}
            {repNumber && (
              <div className="flex items-center gap-1">
                <Typography variant="mediumParagraph" color="gray">
                  Called from:
                </Typography>
                <Typography variant="mediumParagraph">
                  {formatPhoneNumber(repNumber)}
                </Typography>
              </div>
            )}

            {/* Title as secondary info when we have prospect name */}
            {title && prospectName && (
              <div className="flex items-center gap-1">
                <Typography variant="mediumParagraph" color="gray">
                  Title:
                </Typography>
                <Typography variant="mediumParagraph">
                  {title}
                </Typography>
              </div>
            )}

            {/* Company name */}
            {companyName && (
              <div className="flex items-center gap-1">
                <Typography variant="mediumParagraph" color="gray">
                  Company:
                </Typography>
                <Typography variant="mediumParagraph">
                  {companyName}
                </Typography>
              </div>
            )}
            
            {/* Platform - make clickable if URL exists */}
            {platform && <div className="flex items-center gap-1">
              <Typography variant="mediumParagraph" color="gray">
                Platform:
              </Typography>
              {getPlatformUrl() ? (
                <a 
                  href={getPlatformUrl() || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <Typography variant="mediumParagraph">
                    {PLATFORM_TO_HUMAN_READABLE[platform]}
                  </Typography>
                  <ExternalLink size={14} />
                </a>
              ) : (
                <Typography variant="mediumParagraph">
                  {PLATFORM_TO_HUMAN_READABLE[platform]}
                </Typography>
              )}
            </div>}

            {/* Show user who placed the call if different from viewer */}
            {user && data.sales_metric?.user_id !== user.user_id && (
              <div className="flex items-center gap-1">
                <Typography variant="mediumParagraph" color="gray">
                  Rep:
                </Typography>
                <Typography variant="mediumParagraph">
                  {allVisibleUsers.find((u) => u.user_id === getSessionUserId())?.user_name ?? "Deactivated User"}
                </Typography>
              </div>
            )}
          </div>
          
          {/* Disposition, sentiment and purpose row with clickable components */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Disposition - show if there are options or if there's a value */}
            {(dispositionOptions.length > 0 || disposition) && (
              <ClickableMetric
                value={disposition || null}
                options={dispositionOptions}
                placeholder="Set disposition"
                onSelect={(value) => handleMetricUpdate('disposition', value)}
                disabled={!canEdit}
              />
            )}

            {/* Sentiment - show if there are options or if there's a value */}
            {(sentimentOptions.length > 0 || sentiment) && (
              <ClickableMetric
                value={sentiment || null}
                options={sentimentOptions}
                placeholder="Set sentiment"
                onSelect={(value) => handleMetricUpdate('sentiment', value)}
                disabled={!canEdit}
              />
            )}

            {/* Purpose - show if there are options or if there's a value */}
            {(purposeOptions.length > 0 || purpose) && (
              <ClickableMetric
                value={purpose || null}
                options={purposeOptions}
                placeholder="Set purpose"
                onSelect={(value) => handleMetricUpdate('purpose', value)}
                disabled={!canEdit}
              />
            )}
          </div>
        </div>
      </div>

      {/* Webhook Preview Modal */}
      {webhookInfos.length > 0 && (
        <WebhookPreviewModal
          sessionId={data.session.session_id}
          isOpen={isWebhookModalOpen}
          onClose={() => setIsWebhookModalOpen(false)}
        />
      )}
    </div>
  );
}; 